import ApartmentIcon from '@mui/icons-material/Apartment';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useCompanyId } from '../../contexts/CompaniesAreasContext';
import { useGetCompanies } from '../../hooks/companies/list-companies';

function CompanySelect(): JSX.Element {
  const { t } = useTranslation();
  const { companyId, setCompanyId, resetCompanyId, disabled } = useCompanyId();
  const { isLoading, isError, data, refetch } = useGetCompanies(999, 1, { sortModel: [] });

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClickButton = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (): void => {
    setAnchorEl(null);
  };

  const handleCompanyChange = (id: string): void => {
    const selectedCompany = data?.data.find((company) => company.company_id === id);
    if (selectedCompany && id !== 'ALL') {
      setCompanyId({
        id: selectedCompany.company_id,
        name: selectedCompany.name,
        coordinates: [
          {
            lat: selectedCompany.coordinates.latitude,
            lng: selectedCompany.coordinates.longitude,
          },
        ],
        timezone: selectedCompany.timezone,
      });
    } else if (id === 'ALL') {
      setCompanyId({
        id: 'ALL',
        name: 'All',
        coordinates: [
          {
            lat: data ? data.data[0].coordinates.latitude : 0,
            lng: data ? data.data[0].coordinates.longitude : 0,
          },
        ],
        timezone: data ? data.data[0].timezone : '',
      });
    }
    handleClose();
  };

  const setDefaultCompanyId = (): void => {
    if (!isLoading && !isError && data && data.data.length > 0) {
      setCompanyId({
        id: data.data[0].company_id,
        name: data.data[0].name,
        coordinates: [{ lat: data.data[0].coordinates.latitude, lng: data.data[0].coordinates.longitude }],
        timezone: data.data[0].timezone,
      });
    } else {
      resetCompanyId();
    }
  };

  useEffect(() => {
    if (!companyId) {
      setDefaultCompanyId();
    } else {
      if (!isLoading && !isError && data && data.data.length === 0) {
        resetCompanyId();
      }
    }
  }, [companyId, data, isLoading, isError, setCompanyId]);

  useEffect(() => {
    refetch();
  }, []);

  return (
    <>
      {!isLoading && (
        <>
          {companyId && data ? (
            <>
              <Tooltip
                arrow
                title={`${t('select.company')} ${disabled ? ' (' + t('select.scope_disabled') + ')' : ''}`}
                placement="bottom"
              >
                <Button
                  startIcon={<ApartmentIcon />}
                  aria-expanded={open ? 'true' : undefined}
                  sx={{ color: '#fff', cursor: disabled ? 'not-allowed' : 'pointer' }}
                  onClick={disabled ? undefined : handleClickButton}
                >
                  {companyId.name === 'All' ? t('select.all_companies') : companyId.name}
                </Button>
              </Tooltip>

              <Menu
                id="area-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                MenuListProps={{
                  'aria-labelledby': 'area-button',
                  role: 'menu',
                }}
                defaultValue={companyId.id}
              >
                {data.data.length > 1 && (
                  <MenuItem
                    key={'ALL'}
                    value={'ALL'}
                    onClick={() => handleCompanyChange('ALL')}
                    sx={{
                      background: 'ALL' === companyId.id ? '#d9d9d9' : '#fff',
                    }}
                  >
                    {t('select.all_companies')}
                  </MenuItem>
                )}
                {data.data.map((company) => {
                  return (
                    <MenuItem
                      key={company.company_id}
                      value={company.company_id}
                      onClick={() => handleCompanyChange(company.company_id)}
                      sx={{
                        background: company.company_id === companyId.id ? '#d9d9d9' : '#fff',
                      }}
                    >
                      {company.name}
                    </MenuItem>
                  );
                })}
              </Menu>
            </>
          ) : (
            <Link to="/settings/companies/create">
              <Typography
                sx={{ fontSize: '13px', fontWeight: 500, color: '#fff', display: 'flex', alignItems: 'center' }}
                component="span"
              >
                <WarningAmberIcon sx={{ mr: '4px' }} />
                {t('pages.companies.no_company')}
              </Typography>
            </Link>
          )}
        </>
      )}
    </>
  );
}

export default CompanySelect;
